import { ChangeDetectionStrategy, Component, computed, inject, input, model, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { TranslocoModule } from '@jsverse/transloco';
import { ExpandService } from 'libs/shared/util-common/src/lib/expand.service';
import { take } from 'rxjs';
import { GuidelineService, GuidelineViewService } from 'shared/domain';
import { ConfirmationDialogComponent, DialogService, NoResultsComponent } from 'shared/ui-common';
import { compareArrays, compareHtmlText, IsConnectedGuidelinePipe, isTruthy } from 'shared/util-common';

import {
  UpdateStatus,
  FlowchartWidgetLocalChanges,
  ComponentExpandStrategy,
  GuidelineTagStatus,
  TextWidgetLocalChanges,
  GuidelineWidget,
} from '@treetop/shared/model';

import { CompareComponentListWidgetComponent } from './compare-widgets/component-list/compare-component-list-widget.component';
import { CompareTextWidgetComponent } from './compare-widgets/text/compare-text-widget.component';
import { ComponentListWidgetComponent } from './view-widgets/component-list/component-list-widget.component';
import { FlowchartWidgetComponent } from './view-widgets/flowchart/flowchart-widget.component';
import { TextWidgetComponent } from './view-widgets/text/text-widget.component';
import { WorkflowWidgetComponent } from './view-widgets/workflow/workflow-widget.component';

@Component({
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'tt-widget',
  templateUrl: './widget.component.html',
  styleUrls: ['./widget.component.scss'],
  imports: [
    ComponentListWidgetComponent,
    FlowchartWidgetComponent,
    TextWidgetComponent,
    CompareTextWidgetComponent,
    CompareComponentListWidgetComponent,
    WorkflowWidgetComponent,
    NoResultsComponent,
    TranslocoModule,
    IsConnectedGuidelinePipe,
  ],
})
export class WidgetComponent {
  private guidelineService = inject(GuidelineService);
  private dialog = inject(DialogService);
  private expandService = inject(ExpandService);

  protected guidelineViewMode = inject(GuidelineViewService).mode;

  public widgetId = input.required<string>();
  public isEditMode = model<boolean>(false);
  public isCompareMode = model<boolean>(false);
  public allowEditTags = input<boolean>(false);
  public chapterTitle = input<string | null>(null);
  public expandStrategy = input<ComponentExpandStrategy | null>(null);

  protected isMergeConflictResolved = signal(false);
  private originalLocalChanges = signal(undefined);

  protected widget = computed(() => this.guidelineService.selectElement<GuidelineWidget>(this.widgetId())());
  private parentWidgetTag = computed(() => this.guidelineService.selectElement(this.widget().parentId)()?.tag);
  protected guidelineData = computed(() => this.guidelineService.guidelineData());

  constructor() {
    toObservable(this.widget)
      .pipe(isTruthy(), take(1))
      .subscribe((widget) => {
        if (!widget) {
          return;
        }

        if ('localChanges' in widget) {
          this.originalLocalChanges.set(widget.localChanges);
        }

        this.expandService.addItem(widget.uniqueId, true, this.widget()?.parentId);
      });
  }

  public hasLocalChanges = computed(() => {
    const widget = this.widget();
    if (!widget) {
      return false;
    }

    switch (widget?.widgetType) {
      case 'text': {
        return !!widget.localChanges?.text;
      }
      case 'flowchart': {
        return !!widget.localChanges?.elements;
      }
      case 'component-list': {
        /**
         * TODO: Currently the component-list local changes are not propagated to the chapter
         * This means that when there is a change in the component list, the "child updated"/"updated"
         * tag is not shown in the chapter header.
         * This is returning false, to hide the "Edited" label in the chapter header to avoid confusion.
         */
        return false;
      }

      default:
        return false;
    }
  });

  public mergeConflictStatus = computed(() => {
    const widget = this.widget();
    const localChanges = this.originalLocalChanges();

    if (!localChanges) {
      return undefined;
    }

    if (!widget) {
      return undefined;
    }

    if (this.parentWidgetTag()?.status === GuidelineTagStatus.NEW) {
      return undefined;
    }

    if (this.guidelineService.guidelineData().updateStatus !== UpdateStatus.UPDATED_WITH_CONFLICTS) {
      return undefined;
    }

    if (this.isMergeConflictResolved()) {
      return 'resolved';
    }

    if (widget.tag?.status !== GuidelineTagStatus.UPDATED && widget.tag?.status !== GuidelineTagStatus.CHILD_UPDATED) {
      return undefined;
    }

    if (widget.widgetType === 'text') {
      if (!(localChanges as TextWidgetLocalChanges).text) {
        return undefined;
      }

      if (!compareHtmlText(widget.text, (localChanges as TextWidgetLocalChanges).text)) {
        return 'conflict';
      }
    } else if (widget.widgetType === 'flowchart') {
      if (!(localChanges as FlowchartWidgetLocalChanges).elements) {
        return undefined;
      }

      if (
        !compareArrays(widget.elements ?? [], (localChanges as FlowchartWidgetLocalChanges).elements ?? [], {
          ignoreUndefinedMissingKeys: true,
          ignoreOrder: true,
          stringEqualFn: (a, b) => (a ?? '') === (b ?? ''),
        })
      ) {
        return 'conflict';
      }
    }

    return undefined;
  });

  protected handleResolveMergeConflict() {
    this.isMergeConflictResolved.set(true);
    this.guidelineService.forceMarkWidgetChanged(this.widget().uniqueId);
  }

  protected handleUnresolveMergeConflict() {
    this.dialog
      .open(ConfirmationDialogComponent, {
        title: 'SingleGuidelineManager.ImportedGuideline.Editor.UnresolveMergeConflictDialog.Title',
        data: {
          message: 'SingleGuidelineManager.ImportedGuideline.Editor.UnresolveMergeConflictDialog.Message',
          cancelButtonCaption: 'Cancel',
          confirmButtonCaption: 'SingleGuidelineManager.ImportedGuideline.Editor.UnresolveMergeConflictDialog.Confirm',
        },
      })
      .afterClosed()
      .pipe(isTruthy())
      .subscribe(() => {
        this.isMergeConflictResolved.set(false);
        this.guidelineService.updateWidgetLocal(this.widget()?.uniqueId, this.originalLocalChanges());
      });
  }
}
